import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/homepage';
import { articleDetails } from '@/mocks/pages';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) return <NotFound />;

  const detail = articleDetails[slug];

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.majiang-online.com/' },
        { '@type': 'ListItem', position: 2, name: '攻略文章', item: 'https://www.majiang-online.com/article/majiang-rumen-jiaocheng' },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: { '@type': 'Person', name: article.author },
      datePublished: article.publishDate,
      publisher: {
        '@type': 'Organization',
        name: '麻将在线玩',
      },
    },
  ];

  const relatedArticles = detail
    ? articles.filter((a) => detail.relatedArticles.includes(a.slug)).slice(0, 2)
    : articles.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <>
      <SEOHead
        title={`${article.title} - 麻将在线玩免费网页版`}
        description={article.excerpt}
        keywords={`${article.title},麻将攻略,麻将技巧,麻将在线玩免费网页版`}
        canonicalUrl={`https://www.majiang-online.com/article/${slug}`}
        ogType="article"
        schemaData={schemaData}
      />
      <Navbar />
      <main className="pt-16">
        <nav className="w-full max-w-7xl mx-auto px-4 md:px-6 py-4" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-400 flex-wrap">
            <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li><Link to="/article/majiang-rumen-jiaocheng" className="hover:text-primary-500 transition-colors cursor-pointer">攻略文章</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li className="text-foreground-700 font-medium truncate max-w-[200px] md:max-w-none">{article.title}</li>
          </ol>
        </nav>

        <article className="w-full py-8 md:py-12 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <span className="text-xs px-3 py-1.5 bg-primary-100/70 text-primary-700 rounded-full font-medium">
                {article.category}
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400 mb-8 pb-8 border-b border-background-200/70">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                阅读 {article.readTime}
              </span>
            </div>

            {detail ? (
              <div className="prose-custom">
                {detail.content.map((paragraph, idx) => (
                  <p key={idx} className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
                    {paragraph}
                  </p>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-foreground-500">文章内容加载中...</p>
              </div>
            )}

            {detail && detail.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-10 pt-8 border-t border-background-200/70">
                <span className="text-sm text-foreground-500 font-medium">标签：</span>
                {detail.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full border border-background-200/70">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full py-12 md:py-16 bg-background-100">
            <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                  >
                    <span className="text-xs px-2.5 py-1 bg-primary-100/70 text-primary-700 rounded-full font-medium mb-2 inline-block">
                      {ra.category}
                    </span>
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2">{ra.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}